/**
 * Plain-language help text for permission assignment UI tooltips.
 */
const PERMISSION_DESCRIPTIONS = {
  USER_MANAGEMENT:
    "Lets this person open User Management to add, edit, activate, and deactivate staff accounts.",
  SPONSOR_CRO_MANAGEMENT:
    "Lets this person open Sponsor/CRO management to add Sponsor/CRO users, assign study, site, and folder access, and change those users' credentials.",
  CHANGE_CREDENTIALS:
    "Allows resetting another user's password or access code when they cannot sign in.",
  ROLE_MANAGEMENT:
    "Allows creating roles and choosing which permissions belong to each role.",
  SITE_MANAGEMENT:
    "Allows adding and maintaining clinical sites for your organisation.",
  DEPARTMENT_MANAGEMENT:
    "Allows creating and managing departments for your organisation only (add, edit, delete when unused).",
  CAPA_CONFIGURE:
    "Opens CAPA setup screens to configure how corrective actions work in your organisation.",
  QMS_CONFIGURE:
    "Opens QMS Config, including QMS document type and category masters and related QMS setup.",
  EISF_CONFIGURE:
    "Opens eISF Config, including eISF document type and category masters.",
  ADMIN_DASHBOARD:
    "Shows the admin dashboard with organisation-level summary information.",

  STUDY_LIST: "Allows viewing the list of active clinical studies.",
  ARCHIVED_STUDY_LIST:
    "Allows viewing studies that have been archived and are no longer active.",
  ADD_STUDY: "Allows creating new study records.",
  EDIT_STUDY: "Allows changing study details after a study is created.",
  ADD_EDIT_SCHEDULE:
    "Allows creating and updating visit schedules (eSchedule) for studies.",
  CHANGE_STATUS:
    "Allows changing a study's status, such as active, on hold, or closed.",

  ISF_DASHBOARD:
    "Opens the eISF/ePSF dashboard to see investigator site file activity.",
  ISF_REPORTS: "Allows running and viewing eISF/ePSF reports.",
  VIEW_ISF: "Allows opening and reading eISF/ePSF content.",
  CREATE_ISF: "Allows creating a new eISF/ePSF structure for a study.",
  CREATE_FOLDER: "Allows adding folders inside an eISF/ePSF.",
  CREATE_SUB_FOLDER: "Allows adding sub-folders inside an eISF/ePSF folder.",
  UPDATE_FOLDER: "Allows renaming folders in eISF/ePSF.",
  MOVE_FOLDER: "Allows moving folders to a different location in eISF/ePSF.",
  REMOVE_FOLDER: "Allows removing folders from eISF/ePSF.",
  GENERATE_CERTIFICATION:
    "Allows generating training or file certification documents from eISF/ePSF.",
  CREATE_STUDY_ADHOC_PLACEHOLDER:
    "Allows adding one-off placeholder slots on a study eISF/ePSF.",
  EDIT_STUDY_ADHOC_PLACEHOLDER:
    "Allows editing study-specific ad-hoc placeholders.",
  REMOVE_STUDY_ADHOC_PLACEHOLDER:
    "Allows removing study-specific ad-hoc placeholders.",
  FOLDER_LIBRARY:
    "Allows viewing, adding, editing, and deleting shared folder library entries.",
  MILESTONE_LIBRARY:
    "Allows viewing, adding, editing, and deleting milestone templates in the library.",
  MILESTONE_PLAN:
    "Allows viewing, adding, editing, and deleting milestone plans.",
  TEMPLATE_MANAGER:
    "Allows viewing, adding, editing, and deleting eISF templates.",
  AUDIT_LOG:
    "Allows viewing, downloading, and requesting eISF/ePSF audit log exports.",

  VIEW_CAPA: "Allows opening the CAPA list to see corrective actions.",
  ADD_CAPA: "Allows creating new CAPA records.",
  VIEW_REMOVED_CAPA:
    "Allows viewing CAPA records that were removed or archived.",
  CAPA_REPORTS: "Allows viewing CAPA reports and summaries.",
  CAPA_AUDIT_LOG: "Allows viewing the CAPA audit history.",

  BOOK_APPOINTMENT:
    "Allows booking appointments from the list, calendar, occupancy, or volunteer search screens.",
  CANCEL_APPOINTMENT:
    "Allows cancelling appointments from any E-Planner screen where cancel is available.",
  RESCHEDULE_APPOINTMENT:
    "Allows rescheduling appointments, including drag-and-drop on the calendar.",
  CHANGE_APPOINTMENT_STATUS:
    "Allows changing appointment status (for example Arrived or Confirmed) from any E-Planner screen.",
  VIEW_APPOINTMENT_LIST: "Opens the E-Planner appointment list.",
  VIEW_APPOINTMENT_CALENDAR: "Opens the E-Planner appointment calendar.",
  VIEW_LOCATION_OCCUPANCY: "Opens the location occupancy screen.",
  VIEW_APPOINTMENT_CHANGES:
    "Opens the appointment changes screen (cancelled / out-of-window and related changes).",
  VIEW_EPLANNER_ANALYTICS:
    "Opens E-Planner Analytics reports (appointments, volunteers, out-of-window, and deviations).",
  VIEW_WHATSAPP_COMMUNICATION:
    "Opens E-Planner Communication to view and reply to WhatsApp conversations with participants.",

  PARTICIPANT_MANAGER:
    "Opens Participant Manager (active/inactive lists, profile, and add participant).",
  CAMPAIGN_TEMPLATES: "Opens Campaign Templates to create and manage templates.",
  FEASIBILITY_MANAGER:
    "Opens Feasibility Manager and related feasibility communication screens.",
  SEND_BULK_EMAIL_SMS:
    "Opens Send Bulk Email/SMS to message study participants in bulk.",

  QMS_VAULT:
    "Opens My Vault so the user can work with their own QMS documents.",
  QMS_CAN_REVIEW_DOCUMENTS:
    "Allows reviewing QMS documents in workflow and opens Shared With Me for review work.",
  QMS_CAN_APPROVE_DOCUMENTS:
    "Allows approving QMS documents in workflow and opens Shared With Me for approval work.",
  QMS_PUBLISH_DOCUMENTS:
    "Allows publishing approved QMS documents so others can use them.",
  QMS_DOCUMENT_LIBRARY:
    "Opens the document library to browse controlled QMS documents.",
  QMS_LIBRARY_SEE_STAFF:
    "Allows assigning or unassigning staff to document library items.",
  QMS_LIBRARY_ADD_STAFF:
    "Allows assigning or unassigning staff to My Learning training items.",
  QMS_QUIZ_MANAGER:
    "Opens Quiz Manager and allows add/edit/delete quizzes and questions, including bulk import.",
  QMS_DOCUMENT_CONTROL:
    "Opens Document Control and allows archive, unarchive, and change document owner.",
};

export function getPermissionDescription(permissionKey, permissionLabel = "") {
  const key = String(permissionKey || "").trim();
  if (PERMISSION_DESCRIPTIONS[key]) {
    return PERMISSION_DESCRIPTIONS[key];
  }

  const label = String(permissionLabel || "").trim();
  if (!label) {
    return "Controls access to a specific feature in the application.";
  }

  return `Grants access related to "${label}". Turn this on only if the person needs that capability in their day-to-day work.`;
}
